import { useId, useMemo } from 'react';
import type { AreaChartDatum } from '../default/DefaultAreaChart.js';
import { useMeasuredWidth } from '../../hooks/useMeasuredWidth.js';

export interface LuceTrendChartProps {
  /** Same AreaChartDatum shape the Default variant consumes. */
  data: AreaChartDatum[];
  height?: number;
  /** Y-axis + last-point label formatter. */
  formatY?: (n: number) => string;
}

const DEFAULT_FORMAT_Y = (n: number): string =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);

const PADDING = { top: 18, right: 16, bottom: 28, left: 44 };

/**
 * Luce-theme trend chart — an oscilloscope-style instrument readout
 * rather than a filled area. Reticle grid backdrop, thin amber line
 * with a soft glow, pulsing current-value dot, minimal min/max labels
 * on the Y axis.
 *
 * Animations:
 *   - `stroke-dashoffset` on the polyline draws the line from left
 *     to right on mount.
 *   - `transform: scale` on the current-value dot pulses 2.4s.
 * `prefers-reduced-motion: reduce` snaps both to static.
 *
 * Data shape: `AreaChartDatum[]` (same as Default) — `{ x: string, y:
 * number }`. The component extracts numeric Ys for geometry and uses
 * `x` strings as optional x-axis markers at start / middle / end.
 */
export function LuceTrendChart({
  data,
  height = 220,
  formatY = DEFAULT_FORMAT_Y,
}: LuceTrendChartProps) {
  const id = useId().replace(/:/g, '');
  const [wrapRef, W] = useMeasuredWidth<HTMLDivElement>();
  const H = height;
  const innerW = W - PADDING.left - PADDING.right;
  const innerH = H - PADDING.top - PADDING.bottom;

  const geom = useMemo(() => {
    const ys = data.map((d) => d.y);
    if (ys.length === 0 || innerW <= 0) {
      return {
        points: '', areaPath: '', maxY: 0, minY: 0,
        last: null as null | { x: number; y: number; v: number },
        xLabels: [] as { x: number; text: string }[],
        lineLength: 1,
      };
    }
    const maxY = Math.max(...ys);
    const minY = Math.min(0, ...ys);
    const rangeY = maxY - minY || 1;
    const stepX = ys.length > 1 ? innerW / (ys.length - 1) : 0;

    const coords: Array<[number, number]> = ys.map((v, i) => {
      const x = PADDING.left + i * stepX;
      const y = PADDING.top + innerH - ((v - minY) / rangeY) * innerH;
      return [x, y];
    });
    const points = coords.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ');

    // Closed path for the area-fill underneath the line. Same x-axis
    // as the polyline, then drops to the chart baseline at the right
    // edge and closes along the bottom back to the start. Mirrors the
    // Default theme's area-fill shape so the two trend variants share
    // the same vocabulary; Luce keeps its oscilloscope reticle on
    // top, so the fill reads as a tinted backdrop rather than a
    // visual replacement of the line.
    const baselineY = H - PADDING.bottom;
    const areaPath = coords.length > 1
      ? `M${coords[0]![0].toFixed(2)},${baselineY} `
        + coords.map(([x, y]) => `L${x.toFixed(2)},${y.toFixed(2)}`).join(' ')
        + ` L${coords[coords.length - 1]![0].toFixed(2)},${baselineY} Z`
      : '';

    // Approximate length of the polyline so stroke-dashoffset can draw it on mount.
    let lineLength = 0;
    for (let i = 1; i < coords.length; i++) {
      const dx = coords[i]![0] - coords[i - 1]![0];
      const dy = coords[i]![1] - coords[i - 1]![1];
      lineLength += Math.hypot(dx, dy);
    }
    lineLength = Math.max(lineLength, 1);

    const lastIdx = coords.length - 1;
    const last = {
      x: coords[lastIdx]![0],
      y: coords[lastIdx]![1],
      v: ys[lastIdx]!,
    };

    // X labels at start / middle / end — keeps the axis readable
    // even when the data spans 24 points.
    const pickIdx = [0, Math.floor((ys.length - 1) / 2), ys.length - 1]
      .filter((v, i, arr) => arr.indexOf(v) === i); // dedupe
    const xLabels = pickIdx.map((i) => ({
      x: PADDING.left + i * stepX,
      text: data[i]?.x ?? '',
    }));

    return { points, areaPath, maxY, minY, last, xLabels, lineLength };
  }, [data, innerH, innerW, H]);

  return (
    <div
      style={{
        background: 'var(--evi-surface)',
        border: '1.5px solid var(--evi-border-strong)',
        borderRadius: 12,
        padding: 16,
        boxShadow: 'var(--evi-card-glow)',
        fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: 10,
            letterSpacing: 1.4,
            color: 'var(--evi-text-mute)',
            fontWeight: 600,
            textTransform: 'uppercase',
          }}
        >
          Trend
        </span>
        {geom.last && (
          <span
            style={{
              fontSize: 16,
              fontWeight: 400,
              color: 'var(--evi-charging)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatY(geom.last.v)}
          </span>
        )}
      </div>

      <div ref={wrapRef} style={{ width: '100%', height: H }}>
        {W > 0 && (
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        style={{ display: 'block' }}
      >
        {/* Area-fill gradient. Mirrors DefaultAreaChart's stop
            structure (top 22% alpha → bottom 2% alpha) but pulls
            from --evi-charging — Luce's amber accent that the
            line stroke also uses. The fill sits BELOW the reticle
            grid so the oscilloscope vocabulary stays intact;
            without the fill the Luce chart read as "unfinished"
            next to Default. Both themes now share the tinted-
            backdrop convention. */}
        <defs>
          <linearGradient id={`evi-luce-area-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--evi-charging)" stopOpacity="0.28" />
            <stop offset="100%" stopColor="var(--evi-charging)" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {geom.areaPath && (
          <path
            d={geom.areaPath}
            fill={`url(#evi-luce-area-${id})`}
            stroke="none"
          />
        )}

        {/* Reticle grid — 4 horizontal + 6 vertical divisions. Top and
            bottom horizontal lines sit slightly brighter so the chart
            edges read as the instrument frame. */}
        {Array.from({ length: 5 }).map((_, i) => {
          const y = PADDING.top + (innerH * i) / 4;
          return (
            <line
              key={`hgrid-${i}`}
              x1={PADDING.left}
              y1={y}
              x2={W - PADDING.right}
              y2={y}
              stroke="var(--evi-border)"
              strokeWidth="0.6"
              opacity={i === 0 || i === 4 ? 0.7 : 0.3}
            />
          );
        })}
        {Array.from({ length: 7 }).map((_, i) => {
          const x = PADDING.left + (innerW * i) / 6;
          return (
            <line
              key={`vgrid-${i}`}
              x1={x}
              y1={PADDING.top}
              x2={x}
              y2={H - PADDING.bottom}
              stroke="var(--evi-border)"
              strokeWidth="0.6"
              opacity={0.25}
            />
          );
        })}

        {/* Min/max labels — mono, mute, just the two extremes so the
            chart reads without axis clutter. */}
        <text
          x={PADDING.left - 6}
          y={PADDING.top + 4}
          textAnchor="end"
          fill="var(--evi-text-mute)"
          fontSize="9"
          fontFamily="'DM Sans', -apple-system, BlinkMacSystemFont, system-ui, sans-serif"
        >
          {formatY(geom.maxY)}
        </text>
        <text
          x={PADDING.left - 6}
          y={H - PADDING.bottom + 2}
          textAnchor="end"
          fill="var(--evi-text-mute)"
          fontSize="9"
          fontFamily="'DM Sans', -apple-system, BlinkMacSystemFont, system-ui, sans-serif"
        >
          {formatY(geom.minY)}
        </text>

        {/* X-axis markers (start / middle / end datum.x strings) */}
        {geom.xLabels.map((lbl, i) => (
          <text
            key={`xlabel-${i}`}
            x={lbl.x}
            y={H - PADDING.bottom + 16}
            textAnchor={i === 0 ? 'start' : i === geom.xLabels.length - 1 ? 'end' : 'middle'}
            fill="var(--evi-text-mute)"
            fontSize="9"
            fontFamily="'DM Sans', -apple-system, BlinkMacSystemFont, system-ui, sans-serif"
          >
            {lbl.text}
          </text>
        ))}

        {/* Trend line — draw-on via stroke-dashoffset */}
        {data.length > 1 && (
          <polyline
            points={geom.points}
            fill="none"
            stroke="var(--evi-charging)"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              filter: 'drop-shadow(0 0 3px color-mix(in srgb, var(--evi-charging) 55%, transparent))',
              strokeDasharray: geom.lineLength,
              strokeDashoffset: geom.lineLength,
              animation: `evi-luce-trend-draw-${id} 1.4s cubic-bezier(0.22, 1, 0.36, 1) forwards`,
            }}
          />
        )}

        {/* Current value indicator — dashed vertical guide + pulsing
            dot at the most recent point. Pulse scales via transform,
            origin set by translate so it scales around the dot. */}
        {geom.last && (
          <g>
            <line
              x1={geom.last.x}
              y1={geom.last.y}
              x2={geom.last.x}
              y2={H - PADDING.bottom}
              stroke="var(--evi-charging)"
              strokeWidth="0.8"
              strokeDasharray="2 3"
              opacity="0.45"
            />
            <g
              style={{
                transformOrigin: `${geom.last.x}px ${geom.last.y}px`,
                animation: `evi-luce-trend-pulse-${id} 2.4s ease-in-out infinite`,
              }}
            >
              <circle cx={geom.last.x} cy={geom.last.y} r="3.8" fill="var(--evi-charging)" />
            </g>
          </g>
        )}
      </svg>
        )}
      </div>

      <style>{`
        @keyframes evi-luce-trend-draw-${id} {
          to { stroke-dashoffset: 0; }
        }
        @keyframes evi-luce-trend-pulse-${id} {
          0%, 100% { transform: scale(1);   opacity: 1;   }
          50%      { transform: scale(1.35); opacity: 0.7; }
        }
        @media (prefers-reduced-motion: reduce) {
          polyline[style*="evi-luce-trend-draw-${id}"] {
            stroke-dashoffset: 0 !important;
            animation: none !important;
          }
          g[style*="evi-luce-trend-pulse-${id}"] {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}
